import { useEffect, useState } from "react";
import { AlertTriangle, BarChart3, Repeat, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Loader, StatCard } from "./kit";

const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short" });

function MonthlyChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.total));
  return (
    <div className="card p-5" data-testid="insights-monthly">
      <h2 className="font-bold mb-1">Complaints per month</h2>
      <p className="text-xs muted mb-4">Coral shows how many were upheld or partly upheld.</p>
      <div className="flex items-end gap-2 h-44">
        {data.map((d) => (
          <div key={d.month} className="flex-1 flex flex-col items-center gap-1 min-w-0" data-testid={`insights-month-${d.month}`}>
            <span className="text-xs font-semibold">{d.total || ""}</span>
            <div className="w-full rounded-t-md bg-slate-200 relative" style={{ height: `${(d.total / max) * 100}%`, minHeight: d.total ? 6 : 2 }}>
              <div className="absolute bottom-0 inset-x-0 rounded-t-md bg-[#FF8166]" style={{ height: `${d.total ? (d.upheld / d.total) * 100 : 0}%` }} />
            </div>
            <span className="text-[10px] muted">{monthLabel(d.month)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Spark({ values }) {
  const max = Math.max(1, ...values);
  return <div className="flex items-end gap-[2px] h-6 w-28">{values.map((v, i) => <div key={i} className={`flex-1 rounded-sm ${v ? "bg-[#374B5C]" : "bg-slate-100"}`} style={{ height: `${Math.max(v / max, 0.12) * 100}%` }} />)}</div>;
}

function ReasonsTable({ rows }) {
  return (
    <div className="card overflow-x-auto" data-testid="insights-reasons">
      <h2 className="font-bold p-5 pb-2">Top reasons</h2>
      <table className="w-full text-sm"><thead className="bg-slate-50 text-left"><tr><th className="p-3">Reason</th><th className="p-3">Last 12 months</th><th className="p-3">Last 90 days</th><th className="p-3">Total</th><th className="p-3">Upheld</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.reason} className="border-t" data-testid={`insights-reason-${r.reason}`}>
            <td className="p-3"><b>{r.label}</b><span className="block text-xs muted">{r.type}</span></td>
            <td className="p-3"><Spark values={r.months} /></td><td className="p-3 font-semibold">{r.last_90}</td><td className="p-3">{r.total}</td>
            <td className="p-3">{r.total ? Math.round((r.upheld / r.total) * 100) : 0}%</td>
          </tr>))}
          {!rows.length && <tr><td colSpan={5} className="p-6 text-center muted">No complaints yet.</td></tr>}
        </tbody></table>
    </div>
  );
}

function PeopleTable({ title, hint, rows, testid }) {
  return (
    <div className="card overflow-x-auto" data-testid={testid}>
      <div className="p-5 pb-2"><h2 className="font-bold">{title}</h2><p className="text-xs muted">{hint}</p></div>
      <table className="w-full text-sm"><thead className="bg-slate-50 text-left"><tr><th className="p-3">Name</th><th className="p-3">Last 90 days</th><th className="p-3">Total</th><th className="p-3">Upheld</th><th className="p-3">Main reasons</th></tr></thead>
        <tbody>{rows.map((p) => (
          <tr key={p.id} className={`border-t ${p.repeat ? "bg-red-50/60" : ""}`} data-testid={`${testid}-row`}>
            <td className="p-3"><b>{p.name}</b>{p.repeat && <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-100 rounded-full px-2 py-0.5" data-testid="insights-repeat-badge"><Repeat size={11} />Repeat problem</span>}</td>
            <td className="p-3 font-semibold">{p.last_90}</td><td className="p-3">{p.total}</td><td className="p-3">{p.upheld}</td>
            <td className="p-3 text-xs">{p.reasons.map((r) => `${r.label} (${r.count})`).join(", ")}</td>
          </tr>))}
          {!rows.length && <tr><td colSpan={5} className="p-6 text-center muted">Nothing to show yet.</td></tr>}
        </tbody></table>
    </div>
  );
}

export function ComplaintInsights() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/complaints/insights").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!d) return <Loader />;
  const t = d.totals;
  return (
    <div className="space-y-5" data-testid="complaint-insights">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="All complaints" value={t.total} icon={BarChart3} testid="insights-total" />
        <StatCard label="Decided" value={t.decided} icon={CheckCircle2} testid="insights-decided" />
        <StatCard label="Upheld rate" value={`${t.decided ? Math.round((t.upheld / t.decided) * 100) : 0}%`} icon={AlertTriangle} tone="coral" testid="insights-upheld-rate" />
        <StatCard label="Repeat problems" value={t.repeat_count} icon={Repeat} tone="coral" testid="insights-repeat-count" />
      </div>
      <MonthlyChart data={d.monthly} />
      <ReasonsTable rows={d.reasons} />
      <PeopleTable title="Professionals complained about" hint="Repeat problem = 3 or more complaints in the last 90 days." rows={d.providers} testid="insights-providers" />
      <PeopleTable title="Requesters complained about" hint="Homeowners that professionals have complained about." rows={d.requesters} testid="insights-requesters" />
      <PeopleTable title="Most frequent complainants" hint="Helps spot repeated or possibly malicious complaints." rows={d.complainants} testid="insights-complainants" />
    </div>
  );
}
